import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { EXTERNAL_FILE_REQUEST_STATUS, MAX_FILE_SIZE_BYTES, MAX_FILE_SIZE_MB } from "../../../Data/SD";
import { Form, Table } from "react-bootstrap";
import { ExternalFileRequestTokenData, ExternalFileRequestTokenValiationRequest, ExternalFileRequestTokenValiationResponse } from "../../../interfaces/ExternalFileReqeustDtos/externalFileRequestDtos";
import { ExternalFileRequestValidateTokenAsync, UploadExternalFileAsync } from "../../../services/ExternalFileRequestService/ExternalFileRequestService";
import { toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';

// --- Component ---
const ExternalFileUploadPage: React.FC = () => {
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const token = searchParams.get("token");

    const [decodedToken, setDecodedToken] =
        useState<ExternalFileRequestTokenData | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    // NEW: terminalMessage is used when all requests share a final status.
    // When set, we render a simple card with the message and a "Close Window" button.
    const [terminalMessage, setTerminalMessage] = useState<string | null>(null);

    const [requests, setRequests] =
        useState<ExternalFileRequestTokenValiationResponse[] | null>(null);
    const [loading, setLoading] = useState<boolean>(true);

    // map of externalFileReqeustId -> selected File
    const [fileMap, setFileMap] = useState<Record<string, File | null>>({});
    const [uploading, setUploading] = useState<boolean>(false);

    useEffect(() => {
        if (!token) {
            setErrorMessage("⚠️ Please provide a valid token in the URL.");
            setLoading(false);
            return;
        }

        // Decode token (Base64 → JSON)
        try {
            const decodedJson = atob(token);
            const parsed = JSON.parse(decodedJson) as ExternalFileRequestTokenData;
            setDecodedToken(parsed);
        } catch (err) {
            console.error("Invalid token:", err);
            setErrorMessage("❌ Invalid token format. Please check your link.");
            setLoading(false);
            return;
        }
    }, [token]);

    useEffect(() => {
        const validateToken = async () => {
            if (!decodedToken) return;

            try {
                const payload: ExternalFileRequestTokenValiationRequest = {
                    token: token!
                }
                const result = await ExternalFileRequestValidateTokenAsync(payload);

                if (!result.success || !result.data) {
                    setErrorMessage(result.message || "❌ Token validation failed.");
                    return;
                }

                const items = result.data.items;
                setRequests(items);

                // compare using enum only
                const allExpired = items.every(i => i.status == EXTERNAL_FILE_REQUEST_STATUS.EXPIRED);
                const allRevoked = items.every(i => i.status == EXTERNAL_FILE_REQUEST_STATUS.REVOKED);
                const allUploaded = items.every(i => i.status == EXTERNAL_FILE_REQUEST_STATUS.UPLOADED);

                if (allExpired) setTerminalMessage("⏰ All requests are expired.");
                else if (allRevoked) setTerminalMessage("🚫 All requests are revoked.");
                else if (allUploaded) setTerminalMessage("✅ All files have been uploaded.");

            } catch (err) {
                console.error(err);
                setErrorMessage("⚠️ Unable to validate token. Please try again later.");
            } finally {
                setLoading(false);
            }
        };

        validateToken();
    }, [decodedToken, token]);

    // handle file pick for a single row
    // returns true when file accepted, false when rejected (so caller can clear input UI)
    const handleFileChange = (id: string, file?: File | null): boolean => {
        if (!file) {
            setFileMap((prev) => ({ ...prev, [id]: null }));
            return true;
        }

        // Accept PDF only
        const isPdf = file.type === "application/pdf" || /\.pdf$/i.test(file.name);
        if (!isPdf) {
            toast.error(`Only PDF files are allowed. "${file.name}" ignored.`);
            setFileMap((prev) => ({ ...prev, [id]: null }));
            return false;
        }

        // Enforce max size (10 MB)
        if (file.size > MAX_FILE_SIZE_BYTES) {
            toast.error(`"${file.name}" exceeds the maximum size of ${MAX_FILE_SIZE_MB} MB and was ignored.`);
            setFileMap((prev) => ({ ...prev, [id]: null }));
            return false;
        }

        setFileMap((prev) => ({ ...prev, [id]: file }));
        return true;
    };

    // try to get public IP; fallback to 'unknown' when not available
    const fetchPublicIp = async (): Promise<string> => {
        try {
            const resp = await fetch("https://api.ipify.org?format=json");
            if (!resp.ok) return "unknown";
            const data = await resp.json();
            return data?.ip ?? "unknown";
        } catch {
            return "unknown";
        }
    };

    // Upload selected files for all PENDING requests (one button triggers n API calls)
    const handleUploadAll = async () => {
        if (!requests || !token) {
            toast.error("No requests or token available.");
            return;
        }

        const pendingRequests = requests.filter((r) => r.status == EXTERNAL_FILE_REQUEST_STATUS.PENDING);

        if (pendingRequests.length == 0) {
            toast.error("No pending requests to upload.");
            return;
        }

        // ensure every pending request has a file selected
        const missing = pendingRequests.filter((r) => !fileMap[r.externalFileReqeustId]);
        if (missing.length > 0) {
            toast.error(`Please select files for ${missing.length} request(s) before uploading.`);
            return;
        }

        setUploading(true);

        const uploaderIp = await fetchPublicIp();

        const promises = pendingRequests.map((r) =>
            UploadExternalFileAsync(
                fileMap[r.externalFileReqeustId] as File,
                token,
                r.externalFileReqeustId,
                uploaderIp
            ).then((res) => ({ id: r.externalFileReqeustId, res }))
        );

        const results = await Promise.allSettled(promises);

        const updated = [...requests];

        // guard toasts and avoid calling toast with non-string/message objects.
        const processed = new Set<string>();
        results.forEach((settled) => {
            try {
                if (settled.status == "fulfilled") {
                    const { id, res } = settled.value;
                    if (processed.has(id)) return;
                    processed.add(id);

                    const message = typeof res?.message == "string"
                        ? res!.message!
                        : res?.success
                            ? `Uploaded file for request ${id}`
                            : `Upload failed for request ${id}`;

                    if (res && res.success) {
                        // use toastId to prevent duplicates and pass a simple string
                        toast.success(message);
                        const idx = updated.findIndex((x) => x.externalFileReqeustId == id);
                        if (idx >= 0) updated[idx] = { ...updated[idx], status: EXTERNAL_FILE_REQUEST_STATUS.UPLOADED };
                        setFileMap((prev) => ({ ...prev, [id]: null }));
                    } else {
                        toast.error(message);
                    }
                } else {
                    const reason = (settled as PromiseRejectedResult).reason;
                    const id = reason?.id ?? `unknown-${Math.random().toString(36).slice(2, 8)}`;
                    if (processed.has(id)) return;
                    processed.add(id);
                    const msg = reason?.message ?? String(reason);
                    toast.error(`Upload error for ${id}: ${msg}`);
                }
            } catch (e) {
                // swallow toast-related errors to avoid crashing UI (react-toastify internals can throw)
                console.error("Toast error:", e);
            }
        });

        setRequests(updated);
        setUploading(false);
    };

    // compute whether all pending requests have a file selected (used to enable Upload button)
    const pendingRequestsCount = requests ? requests.filter((r) => r.status == EXTERNAL_FILE_REQUEST_STATUS.PENDING).length : 0;
    const allPendingHaveFiles = requests
        ? requests
              .filter((r) => r.status == EXTERNAL_FILE_REQUEST_STATUS.PENDING)
              .every((r) => !!fileMap[r.externalFileReqeustId])
        : false;

    // --- UI Rendering ---
    // single return with conditional content blocks
    return (
        <div className="common_card p-5 text-center">
            {loading ? (
                <div className="p-5">Loading...</div>
            ) : terminalMessage ? (
                <>
                    <div>{terminalMessage}</div>
                    <div className="mt-4 d-flex justify-content-center">
                        <button
                            className="btn btn-secondary"
                            onClick={() => {
                                try {
                                    window.close();
                                    setTimeout(() => {
                                        if (!window.closed) window.location.href = "/";
                                    }, 200);
                                } catch {
                                    window.location.href = "/";
                                }
                            }}
                        >
                            Close Window
                        </button>
                    </div>
                </>
            ) : errorMessage ? (
                <div className="p-5">{errorMessage}</div>
            ) : !requests ? (
                <div className="p-5">⚠️ No requests found for this token.</div>
            ) : (
                <>
                    <h2 className="text-xl font-semibold mb-4 text-left">External File Requests</h2>

                    <Table responsive striped bordered hover className="text-sm">
                        <thead className="bg-gray-100">
                            <tr>
                                <th>Requester</th>
                                <th>File Name</th>
                                <th>Task Title</th>
                                <th>Status</th>
                                <th>Expires On</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {requests.map((r) => (
                                <tr key={r.externalFileReqeustId}>
                                    <td className="align-middle text-left">{r.requesterExmployeeName}</td>
                                    <td className="align-middle text-left">{r.requestedDisplayName}</td>
                                    <td className="align-middle text-left">{r.taskTitle}</td>
                                    <td
                                        className={`align-middle font-medium ${
                                            r.status == EXTERNAL_FILE_REQUEST_STATUS.EXPIRED
                                                ? "text-danger"
                                                : r.status == EXTERNAL_FILE_REQUEST_STATUS.UPLOADED
                                                    ? "text-success"
                                                    : r.status == EXTERNAL_FILE_REQUEST_STATUS.REVOKED
                                                        ? "text-warning"
                                                        : "text-primary"
                                        }`}
                                    >
                                        {r.status}
                                    </td>
                                    <td className="align-middle text-left">
                                        {new Date(r.expiredOn).toLocaleString()}
                                    </td>
                                    <td className="align-middle text-center">
                                        {r.status == EXTERNAL_FILE_REQUEST_STATUS.PENDING ? (
                                            <div className="d-flex align-items-center justify-content-center gap-2">
                                                <Form.Control
                                                    type="file"
                                                    size="sm"
                                                    accept=".pdf"
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                        const file = e.target.files?.[0] ?? null;
                                                        const ok = handleFileChange(r.externalFileReqeustId, file);
                                                        // if validation failed, clear the input so browser doesn't show the filename
                                                        if (!ok) e.currentTarget.value = "";
                                                    }}
                                                />
                                            </div>
                                        ) : (
                                            "-"
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>

                    <div className="mt-4 d-flex gap-2 flex-column align-items-center">
                        <button
                            className="btn btn-primary"
                            onClick={handleUploadAll}
                            disabled={uploading || pendingRequestsCount == 0 || !allPendingHaveFiles}
                        >
                            {uploading ? "Uploading..." : "Upload All Selected"}
                        </button>
                        {pendingRequestsCount > 0 && !allPendingHaveFiles && (
                            <small className="text-muted mt-2">
                                Select files for all {pendingRequestsCount} pending request(s) to enable upload.
                            </small>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default ExternalFileUploadPage;

